import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, adminHeaders, getAdminToken, setAdminToken, clearAdminToken, resolveUrl, CATEGORIES, isPhysical } from "@/lib/api";
import { Lock, Plus, Edit, Trash2, Upload, LogOut, RefreshCw, X, ImageIcon, FileText, ShieldAlert } from "lucide-react";

const EMPTY = {
  name: "", code: "", category: CATEGORIES[0], standard: "", appearance: "",
  specific_gravity: "", ph_value: "", chloride_content: "", dosage: "",
  description: "", image_url: "", datasheet_url: "", msds_url: "",
  dimensions: "", width: "", color: "",
};

const EMPTY_PROJECT = { title: "", location: "", year: "", category: "", description: "", image_url: "" };

function ProductForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [busy, setBusy] = useState("");
  const imgRef = useRef();
  const pdfRef = useRef();
  const msdsRef = useRef();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (file, kind, key) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(kind);
    try {
      const r = await api.post(`/upload/${kind}`, fd, { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, [key]: r.data.url }));
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy("");
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.code) return toast.error("Name and code are required");
    onSave(form);
  };

  const inp = "w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-slate-900 text-sm focus:border-brand-500 outline-none";
  const upBtn = "shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:border-brand-500/60";
  const physical = isPhysical(form.category);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-50/80 backdrop-blur-sm" onClick={onCancel}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white border border-slate-300 rounded-2xl p-7 space-y-4" data-testid="product-form">
        <button type="button" onClick={onCancel} className="absolute top-4 right-4 p-2 text-slate-500 hover:text-slate-900" data-testid="form-close"><X size={20} /></button>
        <h2 className="font-heading text-xl font-bold text-slate-900">{initial ? "Edit Product" : "Add Product"}</h2>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-xs text-slate-500">Name *</label><input value={form.name} onChange={set("name")} data-testid="form-name" className={inp} /></div>
          <div><label className="text-xs text-slate-500">Product Code *</label><input value={form.code} onChange={set("code")} data-testid="form-code" className={inp} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-500">Category</label>
            <select value={form.category} onChange={set("category")} data-testid="form-category" className={inp}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {physical ? (
            <div><label className="text-xs text-slate-500">Dimensions</label><input value={form.dimensions} onChange={set("dimensions")} placeholder="e.g. Length x Width (L x W)" data-testid="form-dimensions" className={inp} /></div>
          ) : (
            <div><label className="text-xs text-slate-500">Standard / Class</label><input value={form.standard} onChange={set("standard")} data-testid="form-standard" className={inp} /></div>
          )}
        </div>

        {physical ? (
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-xs text-slate-500">Depth</label><input value={form.width} onChange={set("width")} placeholder="e.g. 60 mm" data-testid="form-width" className={inp} /></div>
            <div><label className="text-xs text-slate-500">Color</label><input value={form.color} onChange={set("color")} placeholder="e.g. Grey / Sand / Custom" data-testid="form-color" className={inp} /></div>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-xs text-slate-500">Appearance</label><input value={form.appearance} onChange={set("appearance")} className={inp} /></div>
              <div><label className="text-xs text-slate-500">Specific Gravity</label><input value={form.specific_gravity} onChange={set("specific_gravity")} className={inp} /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="text-xs text-slate-500">pH Value</label><input value={form.ph_value} onChange={set("ph_value")} className={inp} /></div>
              <div><label className="text-xs text-slate-500">Chloride Content</label><input value={form.chloride_content} onChange={set("chloride_content")} className={inp} /></div>
            </div>
            <div><label className="text-xs text-slate-500">Dosage Range</label><input value={form.dosage} onChange={set("dosage")} placeholder="e.g. 600 - 2000 ml / 100 kg cement" className={inp} /></div>
          </>
        )}
        <div><label className="text-xs text-slate-500">Description</label><textarea value={form.description} onChange={set("description")} rows={3} data-testid="form-description" className={inp + " resize-none"} /></div>

        <div>
          <label className="text-xs text-slate-500">Product Image</label>
          <div className="mt-1 flex items-center gap-3">
            {form.image_url ? (
              <img src={resolveUrl(form.image_url)} alt="preview" className="w-16 h-16 rounded-lg object-cover border border-slate-300" />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-slate-50 border border-slate-300 flex items-center justify-center text-slate-600"><ImageIcon size={20} /></div>
            )}
            <input value={form.image_url} onChange={set("image_url")} placeholder="Paste image URL or upload" className={inp} />
            <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0], "image", "image_url")} data-testid="form-image-input" />
            <button type="button" onClick={() => imgRef.current.click()} disabled={busy === "image"} className={upBtn} data-testid="form-upload-image"><Upload size={15} /> {busy === "image" ? "..." : "Upload"}</button>
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500">Technical Data Sheet PDF (optional — leave empty to auto-generate)</label>
          <div className="mt-1 flex items-center gap-3">
            <div className="w-16 h-16 rounded-lg bg-slate-50 border border-slate-300 flex items-center justify-center"><FileText size={20} className={form.datasheet_url ? "text-brand-400" : "text-slate-600"} /></div>
            <input value={form.datasheet_url} onChange={set("datasheet_url")} placeholder="Uploaded TDS PDF URL" className={inp} />
            <input ref={pdfRef} type="file" accept="application/pdf" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0], "datasheet", "datasheet_url")} data-testid="form-pdf-input" />
            <button type="button" onClick={() => pdfRef.current.click()} disabled={busy === "datasheet"} className={upBtn} data-testid="form-upload-pdf"><Upload size={15} /> {busy === "datasheet" ? "..." : "Upload"}</button>
          </div>
        </div>

        {!physical && (
          <div>
            <label className="text-xs text-slate-500">Safety Data Sheet MSDS PDF (optional — leave empty to auto-generate)</label>
            <div className="mt-1 flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg bg-slate-50 border border-slate-300 flex items-center justify-center"><ShieldAlert size={20} className={form.msds_url ? "text-gold-400" : "text-slate-600"} /></div>
              <input value={form.msds_url} onChange={set("msds_url")} placeholder="Uploaded MSDS PDF URL" className={inp} />
              <input ref={msdsRef} type="file" accept="application/pdf" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0], "msds", "msds_url")} data-testid="form-msds-input" />
              <button type="button" onClick={() => msdsRef.current.click()} disabled={busy === "msds"} className={upBtn} data-testid="form-upload-msds"><Upload size={15} /> {busy === "msds" ? "..." : "Upload"}</button>
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button type="submit" data-testid="form-save" className="flex-1 px-6 py-3 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold">Save Product</button>
          <button type="button" onClick={onCancel} className="px-6 py-3 rounded-full border border-slate-300 text-slate-900">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function ProjectForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial ? { ...EMPTY_PROJECT, ...initial } : EMPTY_PROJECT);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/upload/image`, fd, { headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Title is required");
    onSave(form);
  };

  const inp = "w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-slate-900 text-sm focus:border-brand-500 outline-none";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-50/80 backdrop-blur-sm" onClick={onCancel}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto bg-white border border-slate-300 rounded-2xl p-7 space-y-4" data-testid="project-form">
        <button type="button" onClick={onCancel} className="absolute top-4 right-4 p-2 text-slate-500 hover:text-slate-900" data-testid="project-form-close"><X size={20} /></button>
        <h2 className="font-heading text-xl font-bold text-slate-900">{initial ? "Edit Project" : "Add Project"}</h2>
        <div><label className="text-xs text-slate-500">Title *</label><input value={form.title} onChange={set("title")} data-testid="project-title" className={inp} /></div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><label className="text-xs text-slate-500">Location</label><input value={form.location} onChange={set("location")} data-testid="project-location" className={inp} /></div>
          <div><label className="text-xs text-slate-500">Year</label><input value={form.year} onChange={set("year")} className={inp} /></div>
          <div><label className="text-xs text-slate-500">Category</label><input value={form.category} onChange={set("category")} placeholder="e.g. Buildings" className={inp} /></div>
        </div>
        <div><label className="text-xs text-slate-500">Description</label><textarea value={form.description} onChange={set("description")} rows={3} data-testid="project-description" className={inp + " resize-none"} /></div>
        <div>
          <label className="text-xs text-slate-500">Project Image</label>
          <div className="mt-1 flex items-center gap-3">
            {form.image_url ? <img src={resolveUrl(form.image_url)} alt="preview" className="w-16 h-16 rounded-lg object-cover border border-slate-300" /> : <div className="w-16 h-16 rounded-lg bg-slate-50 border border-slate-300 flex items-center justify-center text-slate-600"><ImageIcon size={20} /></div>}
            <input value={form.image_url} onChange={set("image_url")} placeholder="Paste image URL or upload" className={inp} />
            <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="project-image-input" />
            <button type="button" onClick={() => imgRef.current.click()} disabled={busy} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:border-brand-500/60" data-testid="project-upload-image"><Upload size={15} /> {busy ? "..." : "Upload"}</button>
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="submit" data-testid="project-save" className="flex-1 px-6 py-3 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold">Save Project</button>
          <button type="button" onClick={onCancel} className="px-6 py-3 rounded-full border border-slate-300 text-slate-900">Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [password, setPassword] = useState("");
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState("products");
  const [projects, setProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [showProjectForm, setShowProjectForm] = useState(false);

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  const loadProjects = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});

  useEffect(() => { if (authed) { load(); loadProjects(); } }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/login", { password });
      setAdminToken(r.data.token);
      setAuthed(true);
      toast.success("Welcome back, admin");
    } catch {
      toast.error("Invalid password");
    }
  };

  const logout = () => { clearAdminToken(); setAuthed(false); };

  const save = async (form) => {
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, form, { headers: adminHeaders() });
        toast.success("Product updated");
      } else {
        await api.post("/products", form, { headers: adminHeaders() });
        toast.success("Product added");
      }
      setShowForm(false); setEditing(null); load();
    } catch {
      toast.error("Save failed — check your admin session");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      await api.delete(`/products/${p.id}`, { headers: adminHeaders() });
      toast.success("Product deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const reset = async () => {
    if (!window.confirm("Reset to default sample products? This deletes all current products.")) return;
    try {
      await api.post("/products/seed", {}, { headers: adminHeaders() });
      toast.success("Products reset to defaults");
      load();
    } catch { toast.error("Reset failed"); }
  };

  const saveProject = async (form) => {
    try {
      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, form, { headers: adminHeaders() });
        toast.success("Project updated");
      } else {
        await api.post("/projects", form, { headers: adminHeaders() });
        toast.success("Project added");
      }
      setShowProjectForm(false); setEditingProject(null); loadProjects();
    } catch { toast.error("Save failed — check your admin session"); }
  };

  const removeProject = async (p) => {
    if (!window.confirm(`Delete ${p.title}?`)) return;
    try {
      await api.delete(`/projects/${p.id}`, { headers: adminHeaders() });
      toast.success("Project deleted");
      loadProjects();
    } catch { toast.error("Delete failed"); }
  };

  if (!authed) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-5 bg-slate-50" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm p-8 rounded-2xl bg-white border border-slate-200">
          <div className="w-12 h-12 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400"><Lock size={22} /></div>
          <h1 className="mt-5 font-heading text-2xl font-bold text-slate-900">Admin Login</h1>
          <p className="mt-2 text-sm text-slate-500">Sign in to manage products, images and data sheets.</p>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" data-testid="admin-password"
            className="mt-6 w-full bg-slate-50 border border-slate-300 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-brand-500 outline-none" />
          <button type="submit" data-testid="admin-login-submit" className="mt-4 w-full px-6 py-3 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold">Sign In</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="text-xs uppercase tracking-widest font-mono text-brand-400">Admin CMS</span>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900">Content Manager</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-300 text-slate-700 text-sm hover:border-red-500/60"><LogOut size={15} /> Logout</button>
        </div>

        <div className="mt-8 flex gap-2 border-b border-slate-200">
          <button onClick={() => setTab("products")} data-testid="admin-tab-products"
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${tab === "products" ? "border-brand-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>Products</button>
          <button onClick={() => setTab("projects")} data-testid="admin-tab-projects"
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${tab === "projects" ? "border-brand-500 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>Projects</button>
        </div>

        {tab === "products" ? (
          <>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={reset} data-testid="admin-reset" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-300 text-slate-700 text-sm hover:border-brand-500/60"><RefreshCw size={15} /> Reset Samples</button>
              <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="admin-add-product" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold text-sm"><Plus size={16} /> Add Product</button>
            </div>
            <div className="mt-6 grid gap-4">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl bg-white border border-slate-200" data-testid={`admin-product-${p.code}`}>
                  <div className="w-16 h-16 rounded-lg bg-slate-200 overflow-hidden shrink-0">
                    {p.image_url && <img src={resolveUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs font-mono text-brand-400">{p.code} • {p.category}</div>
                    <h3 className="font-heading font-semibold text-slate-900 truncate">{p.name}</h3>
                    <p className="text-sm text-slate-500 truncate">{p.description}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => { setEditing(p); setShowForm(true); }} data-testid={`admin-edit-${p.code}`} className="p-2.5 rounded-lg border border-slate-300 text-slate-700 hover:border-brand-500/60"><Edit size={16} /></button>
                    <button onClick={() => remove(p)} data-testid={`admin-delete-${p.code}`} className="p-2.5 rounded-lg border border-slate-300 text-red-400 hover:border-red-500/60"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="mt-6 flex justify-end">
              <button onClick={() => { setEditingProject(null); setShowProjectForm(true); }} data-testid="admin-add-project" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold text-sm"><Plus size={16} /> Add Project</button>
            </div>
            <div className="mt-6 grid gap-4">
              {projects.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl bg-white border border-slate-200" data-testid={`admin-project-${p.id}`}>
                  <div className="w-16 h-16 rounded-lg bg-slate-200 overflow-hidden shrink-0">
                    {p.image_url && <img src={resolveUrl(p.image_url)} alt={p.title} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs font-mono text-brand-400">{[p.category, p.location, p.year].filter(Boolean).join(" • ")}</div>
                    <h3 className="font-heading font-semibold text-slate-900 truncate">{p.title}</h3>
                    <p className="text-sm text-slate-500 truncate">{p.description}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => { setEditingProject(p); setShowProjectForm(true); }} data-testid={`admin-edit-project-${p.id}`} className="p-2.5 rounded-lg border border-slate-300 text-slate-700 hover:border-brand-500/60"><Edit size={16} /></button>
                    <button onClick={() => removeProject(p)} data-testid={`admin-delete-project-${p.id}`} className="p-2.5 rounded-lg border border-slate-300 text-red-400 hover:border-red-500/60"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showForm && <ProductForm initial={editing} onSave={save} onCancel={() => { setShowForm(false); setEditing(null); }} />}
      {showProjectForm && <ProjectForm initial={editingProject} onSave={saveProject} onCancel={() => { setShowProjectForm(false); setEditingProject(null); }} />}
    </div>
  );
}
